import { Excess } from 'scenes/common/types';
import { ExcessCode, MotorCoverType } from 'scenes/motor/types';

export const excessTableRowTitle: {
  [key in ExcessCode]: string;
} = {
  [ExcessCode.BaseAmt]: 'Standard excess',
  [ExcessCode.VoluntaryAmt]: 'Voluntary excess',
  [ExcessCode.UnderAgedDriverExcess]: 'Under 21 years old',
  [ExcessCode.InexperienceExcess]: 'Inexperienced driver excess',
  [ExcessCode.ExcessForDriverInSpecifiedRange]: '21-24 years old',
};

export const excessDescriptionHeading = excessTableRowTitle;

export const isAgeExcess = (code: string | undefined): boolean =>
  code === ExcessCode.UnderAgedDriverExcess ||
  code === ExcessCode.ExcessForDriverInSpecifiedRange;

export const hasAgeExcess = (excesses: Excess[]) =>
  excesses.some((excess) => isAgeExcess(excess.code));

export const getComprehensiveExcesses = (excesses: Excess[]) => {
  let nominatedExcess = 0;
  let inexperienceExcess = 0;

  excesses.forEach(({ code, amount }) => {
    if (code === ExcessCode.BaseAmt || code === ExcessCode.VoluntaryAmt) {
      nominatedExcess += amount;
    } else if (code === ExcessCode.InexperienceExcess) {
      inexperienceExcess = amount;
    }
  });
  return { nominatedExcess, inexperienceExcess };
};

export const getComprehensiveExcessData = (excesses: Excess[]) => {
  const newExcesses: Array<{
    type: string;
    amount: number;
  }> = [];

  const { nominatedExcess, inexperienceExcess } = getComprehensiveExcesses(
    excesses
  );

  if (nominatedExcess) {
    newExcesses.push({ type: 'Nominated excess', amount: nominatedExcess });
  }

  if (inexperienceExcess) {
    newExcesses.push({
      type: 'Inexperienced driver excess',
      amount: inexperienceExcess,
    });
  }

  return newExcesses;
};

export const getThirdPartyStandardExcess = (excesses: Excess[]) => {
  const thirdPartyStandardExcess =
    excesses.filter(
      (ex) =>
        ex.code === ExcessCode.BaseAmt && ex.amount && !isAgeExcess(ex.code)
    ) || [];

  if (thirdPartyStandardExcess.length) {
    const [{ amount }] = thirdPartyStandardExcess;
    return amount;
  }
  return 0;
};

export const getThirdPartyExcessData = (excesses: Excess[]) => {
  const thirdPartyExcess = excesses
    .filter((ex) => ex.code && ex.amount && !isAgeExcess(ex.code))
    .map((ex) => ({
      type: excessTableRowTitle[ex.code as ExcessCode],
      amount: ex.amount,
    }));

  return thirdPartyExcess.length ? thirdPartyExcess : [];
};

export const getExcessData = (excesses: Excess[]) => {
  if (excesses.length && excesses[0].name === MotorCoverType.Comprehensive) {
    return getComprehensiveExcessData(excesses);
  }

  return getThirdPartyExcessData(excesses);
};

export const getAgedExcessData = (excesses: Excess[]) => {
  const agedExcess = excesses
    .filter((ex) => ex.code && ex.amount && isAgeExcess(ex.code))
    .map((ex) => ({
      type: excessTableRowTitle[ex.code as ExcessCode],
      amount: ex.amount,
    }));

  return agedExcess.length ? agedExcess : [];
};
